import { isValid, isWithinInterval } from "date-fns";

/**
 * Listing.
 * Sort and filter HTML cards based on provided JSON data.
 * Please find a sample of the JSON data at the bottom of this file.
 * @author  Cog Design
 * @since   v0.0.1
 * @version v1.0.0  Saturday, September 21st, 2024.
 * @global
 */

export default class Listing {
    /**
     * Creates an instance of Listing and initialise component.
     * @param {*} props
     * @memberof Listing
     */
    constructor(props) {
        /**
         * Props currently include:
         * @var {HTMLElement} root The section element containing the filters form. Default `listing-items`
         * @var {String} json_data The ID of the <script> tag containing the JSON data. Default `json-data`.
         * @var {String} card_id The selector identifying the cards elements. Default `[data-id]`.
         * @var {String} daterange The input name to treat as a date range.
         * @var {Function} callback Any method to run after filtering.
         *
         */
        this.el = props.root;
        this.container = props.listing || "listing-items";
        this.callback = props.callback;
        this.daterange = props.daterange || "";
        this.json_data = props.json_data || "json-data";
        this.card_id = props.card_id || "[data-id]";

        this.init();
    }

    /**
     * Parse JSON data and setup event listeners
     *
     * @return {void}
     */
    init() {
        // The filters form
        this.form = this.el.querySelector("form");

        // SSR items container
        this.container = document.getElementById(this.container);

        if (!this.container || !this.form) return;

        // Default items (including promo)
        this.default_listing = this.container.innerHTML;

        // Fetch and parse SSR JSON data
        const json = document.getElementById(this.json_data);

        if (!json) return;

        // Parse JSON data
        this.data = JSON.parse(json.text);

        // Filtered items placeholder
        this.filtered = [];

        // SSR HTML card items
        this.cards = this.container.querySelectorAll(this.card_id);

        // Callback bound to this class
        const filterItems = this.filterItems.bind(this);

        // data-live="true" will run the filters upon input value changing.
        const live_submit = this.form.dataset.live === "true";

        // Trigger a change event upon resetting the form
        // Resets the listing content to its original state
        this.form.addEventListener("reset", (e) => {
            setTimeout(() => {
                this.form.dispatchEvent(new Event("change"));
                this.container.innerHTML = this.default_listing;
            }, 50);
        });

        // Attach change event to form
        const event = live_submit ? "change" : "submit";
        this.form.addEventListener(event, filterItems);
    }

    filterItems(e) {
        // Get current form data
        let form_data = new FormData(this.form);

        // Build query dictionary
        let query = {};
        for (const pair of form_data.entries()) {
            console.log("Pair: ", pair);
            if (pair[1] && pair[1] != "undefined") {
                query[pair[0]] = form_data.getAll(pair[0]); //pair[1];
            }
        }

        this.query = query;
        console.log("Query: ", query);

        if (Object.values(query).length) {
            // Filter JSON data
            this.filtered = this.data.filter((item) => {
                let match = [];

                Object.keys(query).forEach((name) => {
                    // Placeholder for matching nodes
                    let matching = new Set();
                    let isArray = Array.isArray(item[name]) ? true : false;

                    if (this.daterange == name) {
                        // Date range field

                        // Split date range into start, end
                        let [start, end] = query[name].join("").split("/");
                        if (!end) end = start;

                        // Find a match
                        let dates = item[name].filter((date) => {
                            return isWithinInterval(date, {
                                start: start,
                                end: end,
                            });
                        });
                        let search = dates.length ? true : false;
                        match.push(search);
                    } else {
                        // Default matching

                        // Data provided by third party might be separated by a comma
                        // Let's split that into an Array
                        let input_value = query[name][0].split(",");

                        // Create the search Set from query data
                        let search = new Set(input_value);

                        // Create the matching set from JSON data
                        if (isArray) {
                            matching = new Set(item[name]);
                        } else {
                            matching.add(item[name]);
                        }

                        // Find data intersaction
                        let intersection = new Set(
                            [...matching].filter((x) => search.has(x)),
                        );
                        intersection = intersection.size ? true : false;
                        match.push(intersection);

                        // Unsupported in Safari 16 - core-js update?
                        // match.push(!matching.isDisjointFrom(search));
                    }
                });

                // Return matching nodes only
                return !match.includes(false);
            });
        } else {
            this.filtered = this.data;
        }

        // Sort by date
        let sorted = this.filtered.sort(
            (a, b) => new Date(a[this.date]) - new Date(b[this.date]),
        );

        // Pluck IDs
        this.filtered = sorted.map((item) => item.id);

        // Render filtered items
        this.render();

        // Eventually run the attached callback
        if (this.callback) {
            this.callback();
        }

        // Prevent default form submission
        return false;
    }

    render() {
        // Reset container
        this.container.innerHTML = "";

        if (!Object.keys(this.query).length) {
            this.container.innerHTML = this.default_listing;
            this.container.classList.remove("filtered");
        } else {
            this.container.classList.add("filtered");
            // Sort relevant items
            [...this.cards]
                .sort((a, b) => {
                    return (
                        this.filtered.indexOf(a.dataset.id) -
                        this.filtered.indexOf(b.dataset.id)
                    );
                })
                .forEach((item) => {
                    // Append matching element to container
                    if (this.filtered.includes(item.dataset.id)) {
                        this.container.append(item);
                    }
                });
        }
    }
}

/**
 * JSON data sample
 * Keys to match the relevant form input name.
 * 
 * id : unique card identifier (WP slug)
 * date : for sorting (next instance timestamp)
 * dates : performances dates
 * venue/genre/accessibility : taxonomy terms slugs
 * interval : time range string based on performance dates
 * 
  [{
        "id": "event-1",
        "date": "20240730180000",
        "dates": [
            "2024-09-25"
        ],
        "venue": "playhouse",
        "genre": [
            "drama"
        ],
        "accessibility": [
            "audio-described"
        ],
        "interval": [
            "this-week",
            "next-week",
            "this-month"
        ]
    },
    ...]
 */
